'use client';

import { Button, Grid, Heading, InlineCheckbox, TextField } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useEffect, useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { addAddress, getPodsAddressIdFromResponse, toPodsAddressId, updateAddress } from '@libs/api-proxy-client';
import type { AccountAddressesSuccessPartialResponse, AddAddressRequest } from '@libs/common-next';
import { BANNERS, getNextApiRoute, handleSetPhoneNumber, logSentryError, SentryAcpAddressesErrors as SentryErrors, useAlertBanner, useDeviceType, useUserSession } from '@libs/common-next';
import { getCountry } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';
import { PROFILE_DATA } from '../../constants';
import { AddressForm } from '../AddressForm';
import { ExperianModalRHF } from '../ExperianModal';
import type { ExperianResponseLayout } from '../ExperianModal/types';
import { validateExperianAddressLayout } from '../ExperianModal/utils';
import { GenericButton } from '../GenericButton';
import { NameFields } from '../NameFields';
import { ConfirmAutoshipDefaultAddressChangeModal } from './ConfirmAutoshipDefaultAddressModal/ConfirmAutoshipDefaultAddressChangeModal';
import LinkedAutoshipAlert from './LinkedAutoshipAlert/LinkedAutoshipAlert';
import { RemoveAddressModal } from './RemoveAddressModal';
import type { AddAddressFormProps, AddressesPageInterface, AddressFormProps } from './types';
import { getInitialFormState as buildInitialFormState, initialAddressFormState, useAddressForm } from './useAddressForm';
import { trackErrorAddress } from './utils';
import './AddAddressForm.scss';
type ShouldShowAutoshipModalParams = {
  willBeDefault: boolean;
  isPreviousDefaultLinkedToSubscriptions: boolean;
  isChangingDefault: boolean;
};
const shouldShowUpdateAutoshipModal = ({
  willBeDefault,
  isPreviousDefaultLinkedToSubscriptions,
  isChangingDefault
}: ShouldShowAutoshipModalParams): boolean => willBeDefault && isPreviousDefaultLinkedToSubscriptions && isChangingDefault;
const componentClassname = 'add-address-form';
export const getInitialFormState = (address: AccountAddressesSuccessPartialResponse, isPrimaryAddress: boolean) => buildInitialFormState(address, isPrimaryAddress);
export const convertFormDataToPayload = (formData: AddressFormProps) => {
  const {
    address,
    firstName,
    lastName,
    isPrimary,
    phone
  } = formData;
  return {
    firstName,
    lastName,
    countryAbbreviation: getCountry(),
    stateProvinceAbbreviation: address.state,
    zipPostalCode: address.zipCode,
    city: address.city,
    streetLine1: address.streetAddress,
    streetLine2: address.apt,
    isActive: true,
    isPrimary,
    phoneNumber: phone,
    addressType: 'other'
  };
};
export function AddAddressForm({
  syncedAddresses,
  isPrimaryAddress,
  setShowForm,
  onSuccessfulAddressSave,
  currentAddressToEdit,
  isAutoship = false,
  onBack,
  onSaveAddress,
  onError,
  isAddressUsedByOrders = false,
  onSyncAddressToSubscriptionOrders,
  onRemoveAddressClick,
  isPreviousDefaultLinkedToSubscriptions = false,
  previousDefaultAddress = null,
  isFraudValidationNeeded = false,
  onSyncSubscriptionOrdersFromAddress,
  onExperianModalVisibilityChange,
  onSaveComplete,
  onLoadingChange,
  forceDefaultShippingAddress = false,
  deliveryInstructions
}: Readonly<AddAddressFormProps>) {
  const {
    isMobile
  } = useDeviceType();
  const {
    setAlert
  } = useAlertBanner();
  const [confirmDefaultSyncLoadingMode, setConfirmDefaultSyncLoadingMode] = useState<'keep' | 'update' | undefined>();
  const [isConfirmDefaultAddressSyncModalOpen, setIsConfirmDefaultAddressSyncModalOpen] = useState(false);
  const [pendingSelectedAddress, setPendingSelectedAddress] = useState<AddressFormProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);
  const {
    userData,
    updateProfileData
  } = useUserSession();
  const [experianResponse, setExperianResponse] = useState<ExperianResponseLayout | null>(null);
  useEffect(() => {
    setAlert(null);
  }, [setAlert]);
  const isEditing = !!currentAddressToEdit;
  const isAddressLinkedToAutoship = isEditing && (currentAddressToEdit?.hasSubscription || isAddressUsedByOrders);
  const methods = useAddressForm({
    currentAddressToEdit,
    isPrimaryAddress
  });
  const {
    formState: {
      errors,
      isSubmitted,
      touchedFields
    },
    setValue
  } = methods;
  const isCheckboxDisabled = (isPrimary: boolean) => isPrimary && (isEditing ? userData?.addresses?.length === 1 : !userData?.addresses || userData.addresses.length === 0);
  const isDefaultCheckboxDisabled = (isPrimary_0: boolean, useForAllShipments: boolean) => forceDefaultShippingAddress || isCheckboxDisabled(isPrimary_0) || isAutoship && useForAllShipments || isAutoship && isEditing && !!currentAddressToEdit?.isPrimary;
  const handleUseForAllShipmentsChange = (useForAllShipments_0: boolean) => {
    const newValue = !useForAllShipments_0;
    setValue('useForAllShipments', newValue, {
      shouldDirty: true
    });
    if (newValue || forceDefaultShippingAddress) {
      setValue('isPrimary', true, {
        shouldDirty: true
      });
    } else {
      const isEditingDefaultAddress = isAutoship && isEditing && !!currentAddressToEdit?.isPrimary;
      setValue('isPrimary', isEditingDefaultAddress, {
        shouldDirty: true
      });
    }
  };
  const handleAutoshipCheckboxMouseDown = (event: MouseEvent<HTMLElement>) => {
    // Keep first click on autoship checkboxes from being consumed by blur validation.
    if (isAutoship) {
      event.preventDefault();
    }
  };
  const handleInvalidFormSubmit = () => {
    onLoadingChange?.(false);
    if (!isAutoship) {
      setAlert({
        ...BANNERS.invalidShippingAddress
      });
    }
    trackErrorAddress(BANNERS.invalidShippingAddress.header);
  };
  const syncAddressToSubscriptionOrdersIfNeeded = async (payload: AddAddressRequest, savedAddressId: string | undefined): Promise<void> => {
    const shouldSync = isEditing && isAddressUsedByOrders && onSyncAddressToSubscriptionOrders;
    if (!shouldSync) return;
    try {
      await onSyncAddressToSubscriptionOrders(payload, savedAddressId);
    } catch (error) {
      logSentryError(error, SentryErrors.AddAddressForm.SubscriptionSyncFailed);
    }
  };
  const syncPreviousDefaultSubscriptionsToNewAddress = async (payload_0: AddAddressRequest, savedAddressId_0: string | undefined): Promise<void> => {
    if (!previousDefaultAddress || !onSyncSubscriptionOrdersFromAddress) return;
    try {
      await onSyncSubscriptionOrdersFromAddress(previousDefaultAddress, payload_0, savedAddressId_0);
    } catch (error_0) {
      logSentryError(error_0, SentryErrors.AddAddressForm.SubscriptionSyncFailed);
    }
  };
  const saveSelectedAddress = async (selectedAddress: AddressFormProps) => {
    const selectedAddressForSave = forceDefaultShippingAddress ? {
      ...selectedAddress,
      isPrimary: true
    } : selectedAddress;
    const willBeDefault = selectedAddressForSave.isPrimary ?? userData?.addresses?.length === 0;
    const isChangingDefault = !isEditing || !currentAddressToEdit?.isPrimary;
    if (shouldShowUpdateAutoshipModal({
      willBeDefault,
      isPreviousDefaultLinkedToSubscriptions,
      isChangingDefault
    })) {
      setPendingSelectedAddress(selectedAddressForSave);
      setIsConfirmDefaultAddressSyncModalOpen(true);
      return;
    }
    await handleSaveAddress(selectedAddressForSave, false);
  };
  const handleSaveAddress = async (selectedAddress_0: AddressFormProps, syncPreviousDefaultToNewAddress: boolean) => {
    try {
      setIsLoading(true);
      if (onSaveAddress) {
        await onSaveAddress(selectedAddress_0);
      } else {
        const payload_1 = {
          ...convertFormDataToPayload(selectedAddress_0),
          isPrimary: selectedAddress_0.isPrimary ?? userData?.addresses?.length === 0,
          ...deliveryInstructions?.getPayload()
        };
        const response = isEditing ? await updateAddress(currentAddressToEdit?.addressId, payload_1) : await addAddress(payload_1);
        if (response) {
          deliveryInstructions?.onSaved(response);
        }
        if (response && updateProfileData) {
          /** Function to update the address data inside the UserSessionWrapper */
          await updateProfileData?.(PROFILE_DATA.ADDRESSES);
          if (!isFraudValidationNeeded) {
            const savedAddressId_1 = getPodsAddressIdFromResponse(response) ?? (isEditing ? toPodsAddressId(currentAddressToEdit?.addressId) : undefined);
            await syncAddressToSubscriptionOrdersIfNeeded(payload_1, savedAddressId_1);
            if (syncPreviousDefaultToNewAddress) {
              await syncPreviousDefaultSubscriptionsToNewAddress(payload_1, savedAddressId_1);
            }
          }
          onSuccessfulAddressSave({
            isAddressLinkedToAutoship
          });
          await updateProfileData?.(PROFILE_DATA.PROFILE);
        }
      }
      setIsConfirmDefaultAddressSyncModalOpen(false);
      setConfirmDefaultSyncLoadingMode(undefined);
      setPendingSelectedAddress(null);
      setIsLoading(false);
    } catch (err) {
      logSentryError(err, SentryErrors.AddAddressForm.SubmitFailed);
      SnackbarUtils.showPredefined('SERVER_ERROR');
      setIsLoading(false);
      if (onSaveAddress) {
        throw err;
      }
    }
  };
  const validateAddressAndSubmitForm = async (values: AddressesPageInterface) => {
    try {
      setIsLoading(true);
      setAlert(null);
      if (deliveryInstructions && !(await deliveryInstructions.validate())) {
        setIsLoading(false);
        return;
      }
      const addressValues = values.address;
      const experianResponseValue = await validateExperianAddressLayout(addressValues);
      setExperianResponse(experianResponseValue);
      if (!experianResponseValue?.result?.confidence) {
        throw new Error();
      }
      onSaveComplete?.();
      setIsLoading(false);
    } catch (err_0) {
      logSentryError(err_0, SentryErrors.AddAddressForm.SubmitFailed);
      SnackbarUtils.showPredefined('SERVER_ERROR');
      setIsLoading(false);
      onError?.();
    }
  };
  const handleConfirmAutoshipDefaultAddressChangeKeepExisting = async () => {
    if (!pendingSelectedAddress) {
      setIsConfirmDefaultAddressSyncModalOpen(false);
      return;
    }
    setConfirmDefaultSyncLoadingMode('keep');
    try {
      await handleSaveAddress(pendingSelectedAddress, false);
    } catch {
      onError?.();
    }
  };
  const handleConfirmAutoshipDefaultAddressChangeUpdate = async () => {
    if (!pendingSelectedAddress) {
      setIsConfirmDefaultAddressSyncModalOpen(false);
      return;
    }
    setConfirmDefaultSyncLoadingMode('update');
    try {
      await handleSaveAddress(pendingSelectedAddress, true);
    } catch {
      onError?.();
    }
  };
  const [address, phone, isPrimary_1, useForAllShipments_1] = useWatch({
    control: methods.control,
    name: ['address', 'phone', 'isPrimary', 'useForAllShipments']
  });
  const values_0 = {
    address: address ?? initialAddressFormState,
    phone: phone ?? '',
    isPrimary: isPrimary_1 ?? false,
    useForAllShipments: useForAllShipments_1 ?? false
  } as AddressesPageInterface;
  const phoneField = methods.register('phone');
  return <FormProvider {...methods}>
            <>
                <form className={componentClassname} onSubmit={methods.handleSubmit(validateAddressAndSubmitForm, handleInvalidFormSubmit)}>
                    <RemoveAddressModal syncedAddresses={syncedAddresses} closeModal={() => {
          setIsRemoveModalOpen(false);
        }} isActive={isRemoveModalOpen} addressId={currentAddressToEdit?.addressId} isFraudValidationNeeded={isFraudValidationNeeded} closeForm={() => {
          setShowForm(false);
          onSuccessfulAddressSave?.();
        }} />
                    <ExperianModalRHF experianResponse={experianResponse} saveSelectedAddress={selectedAddress_1 => saveSelectedAddress({
          ...selectedAddress_1,
          phone: selectedAddress_1.phone ?? ''
        })} loading={isLoading} setIsLoading={setIsLoading} onEditAddressClick={isAutoship ? () => setExperianResponse(null) : undefined} onVisibilityChange={onExperianModalVisibilityChange} onSaveError={onError} isAutoship={isAutoship} />
                    <Heading tagName="h5">Addresses</Heading>
                    <Grid gap="sm" className={`${componentClassname}__body`}>
                        <Grid.Item>
                            <Heading tagName="h3" className={`${componentClassname}__sub-heading`}>
                                {isEditing ? 'Edit address' : 'Add address'}
                            </Heading>
                        </Grid.Item>

                        {isAddressLinkedToAutoship && <Grid.Item>
                                <LinkedAutoshipAlert isFraudValidationNeeded={isFraudValidationNeeded} />
                            </Grid.Item>}

                        <NameFields formLibrary="rhf" />
                        <Grid.Item>
                            <AddressForm addressSuggestionRoute={getNextApiRoute('address/suggest-address')} aptFieldDefaultOpen={!!values_0.address.apt} formLibrary="rhf" />
                        </Grid.Item>
                        <Grid.Item>
                            <TextField data-cs-mask data-testid="phone" id="phone" label="Phone number" name={phoneField.name} ref={phoneField.ref} maxLength={14} type="tel" value={values_0.phone} onBlur={phoneField.onBlur} onChange={e => {
              handleSetPhoneNumber(e, (_, value) => {
                setValue('phone', value ?? '', {
                  shouldDirty: true
                });
              });
            }} isError={!!((touchedFields.phone || isSubmitted) && errors.phone)} fieldNote={touchedFields.phone || isSubmitted ? errors.phone?.message : undefined} />
                        </Grid.Item>
                        <Grid.Item>
                            <InlineCheckbox key={`isPrimary-${values_0.isPrimary ?? false}`} disabled={isDefaultCheckboxDisabled(values_0.isPrimary ?? false, values_0.useForAllShipments ?? false)} checked={forceDefaultShippingAddress || !!(values_0.isPrimary ?? false)} label="Set as default shipping address" onMouseDown={handleAutoshipCheckboxMouseDown} onChange={() => {
              if (!values_0.useForAllShipments && !forceDefaultShippingAddress) {
                setValue('isPrimary', !(values_0.isPrimary ?? false), {
                  shouldDirty: true
                });
              }
            }} className={cn(isDefaultCheckboxDisabled(values_0.isPrimary ?? false, values_0.useForAllShipments ?? false) && `${componentClassname}__checkbox--disabled`)} />
                        </Grid.Item>
                        {isAutoship && <Grid.Item className="sparky-u-margin-bottom-sm">
                                <InlineCheckbox checked={values_0.useForAllShipments ?? false} label="Use address for all shipments" onMouseDown={handleAutoshipCheckboxMouseDown} onChange={() => handleUseForAllShipmentsChange(values_0.useForAllShipments ?? false)} />
                            </Grid.Item>}
                        {deliveryInstructions?.section}
                        <Grid.Item>
                            <div className={`${componentClassname}__cta-container`}>
                                <GenericButton disabled={isLoading} className={`${componentClassname}__cta`} data-testid="cancel" fullWidth={isMobile} text="Cancel" size="lg" type="button" onClick={e_0 => {
                e_0.stopPropagation();
                e_0.preventDefault();
                setAlert(null);
                if (isAutoship && onBack) {
                  onBack(e_0);
                } else {
                  setShowForm(false);
                }
              }} />

                                <GenericButton loading={isLoading} disabled={isLoading} className={`${componentClassname}__cta`} data-testid="saveChanges" fullWidth={isMobile} text="Save" variant="primary" size="lg" type="submit" />
                            </div>
                        </Grid.Item>
                    </Grid>
                </form>
                {isEditing && <Button data-testid="remove-address-form-cta" onClick={() => {
        if (isAutoship && onRemoveAddressClick) {
          onRemoveAddressClick();
        } else if (!isAutoship) {
          setIsRemoveModalOpen(true);
        }
      }} text="Remove address" variant="link" color="red" className={`${componentClassname}__remove-address`} />}

                <ConfirmAutoshipDefaultAddressChangeModal isActive={isConfirmDefaultAddressSyncModalOpen} loading={confirmDefaultSyncLoadingMode} isFraudValidationNeeded={isFraudValidationNeeded} onClose={() => {
        setIsConfirmDefaultAddressSyncModalOpen(false);
        setPendingSelectedAddress(null);
      }} onKeepExistingAddress={() => {
        if (isFraudValidationNeeded) {
          setIsConfirmDefaultAddressSyncModalOpen(false);
          setPendingSelectedAddress(null);
        } else {
          handleConfirmAutoshipDefaultAddressChangeKeepExisting();
        }
      }} onUpdateAddress={() => {
        if (isFraudValidationNeeded) {
          handleConfirmAutoshipDefaultAddressChangeKeepExisting();
        } else {
          handleConfirmAutoshipDefaultAddressChangeUpdate();
        }
      }} />
            </>
        </FormProvider>;
}